import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";

import { checkIfoodShippingStatus } from "../../api/shipping";

import {
  alpha,
  Box,
  Card,
  Chip,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import FastfoodIcon from "@mui/icons-material/Fastfood";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { useDispatch, useSelector } from "react-redux";
import { selectOrderById } from "../../store/actions/ordersSlice";
import CheckShippingModal from "./ifoodCheckShipping";
import { ButtonInfoNoShadow } from "../styled/button";
import UberService from "../../api/uber";
import { formatFinance } from "../../utils/formatters";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

const deliveryProviders = [
  {
    id: "ifood",
    name: "iFood",
    logo: "/images/ifood-easy-delivery.png",
    description: "Use o iFood",
    color: "#EA1D2C",
    icon: <FastfoodIcon fontSize="small" />,
  },
  {
    id: "uber",
    name: "Uber",
    logo: "/images/uber-logo-small.png",
    description: "Use o Uber Direct",
    color: "#232323",
    icon: <DeliveryDiningIcon fontSize="small" />,
  },
];

const ShippingProvidersHorizontal = ({
  ordersToDispatch,
  quoteData,
  setQuoteData,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const [loadingQuotes, setLoadingQuotes] = useState({});
  const [quotes, setQuotes] = useState({});
  const [quoteId, setQuoteId] = useState(null);
  const [merchantId, setMerchantId] = useState("");
  const [showIfoodShipping, setShowIfoodShipping] = useState(false);
  const [confirmingOrder, setConfirmingOrder] = useState(false);

  const order = useSelector((state) =>
    selectOrderById(state, ordersToDispatch[0])
  );

  const createQuoteForProvider = useCallback(
    async (providerId, orderId) => {
      setLoadingQuotes((prev) => ({ ...prev, [providerId]: true }));

      try {
        if (providerId === "ifood") {
          if (!merchantId && order.channel !== "IFOOD") {
            setShowIfoodShipping(true);
            return;
          }

          const params = {
            oid: order.order_id,
            mid: merchantId || "",
            lat: order.LAT,
            lng: order.LONG,
          };

          const { data } = await checkIfoodShippingStatus(params);

          setQuotes((prev) => ({
            ...prev,
            [providerId]: {
              quoteId: data.channel_quote_id,
              value: data.net_value,
              minTime: data.min_delivery_time,
              maxTime: data.max_delivery_time,
              available: true,
            },
          }));
        } else if (providerId === "uber") {
          const quoteData = {
            order_id: orderId || order.order_id,
          };

          const { data } = await UberService.createQuote(quoteData);

          setQuotes((prev) => ({
            ...prev,
            [providerId]: {
              quoteId: data.quote_id,
              channelQuoteId: data.channel_quote_id,
              value: data.net_value,
              grossValue: data.gross_value,
              minTime: data.min_delivery_time,
              maxTime: data.max_delivery_time,
              distance: data.distance,
              pickupDuration: data.pickup_duration,
              expirationAt: data.expiration_at,
              available: true,
            },
          }));
        } else if (providerId === "indrive") {
          await new Promise((resolve) => setTimeout(resolve, 800));

          const mockupData = {
            quoteId: `indrive-${Date.now()}`,
            channelQuoteId: `IND-${Math.floor(
              100000 + Math.random() * 900000
            )}`,
            value: (order.total_value * 0.7).toFixed(2),
            grossValue: (order.total_value * 0.8).toFixed(2),
            minTime: 1800,
            maxTime: 2700,
            distance: order.distance || Math.floor(2 + Math.random() * 8),
            pickupDuration: 900,
            expirationAt: new Date(Date.now() + 30 * 60000).toISOString(),
            available: Math.random() > 0.2,
          };

          setQuotes((prev) => ({
            ...prev,
            [providerId]: {
              ...mockupData,
            },
          }));
        }
      } catch (e) {
        console.error(`Error creating quote for ${providerId}:`, e);
        setQuotes((prev) => ({
          ...prev,
          [providerId]: {
            available: false,
            error: e.response?.data?.error || "Failed to create quote",
          },
        }));
      } finally {
        setLoadingQuotes((prev) => ({ ...prev, [providerId]: false }));
      }
    },
    [merchantId, order]
  );

  useEffect(() => {
    if (!order || !ordersToDispatch.length) return;

    const fetchAllQuotes = async () => {
      const initialLoading = {};
      deliveryProviders.forEach((provider) => {
        if (!provider.disabled) {
          initialLoading[provider.id] = true;
        }
      });
      setLoadingQuotes(initialLoading);

      for (const provider of deliveryProviders) {
        if (!provider.disabled) {
          if (provider.id !== deliveryProviders[0].id) {
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
          createQuoteForProvider(provider.id, ordersToDispatch[0]);
        }
      }
    };

    fetchAllQuotes();
  }, [ordersToDispatch, createQuoteForProvider, order]);

  const getEstimatedTime = (providerId) => {
    if (!quotes[providerId] || !quotes[providerId].available) return null;

    if (providerId === "uber" || providerId === "indrive") {
      return Math.round(quotes[providerId].maxTime);
    }

    return Math.round(
      (quotes[providerId].minTime + quotes[providerId].maxTime) / 2 / 60
    );
  };

  const isProviderSelected = (providerId) => {
    return quoteData?.selected && quoteData?.providerId === providerId;
  };

  const isAnotherProviderSelected = (providerId) => {
    return quoteData?.selected && quoteData?.providerId !== providerId;
  };

  const handleProviderClick = (provider) => {
    if (provider.disabled || isAnotherProviderSelected(provider.id)) return;

    if (!quotes[provider.id]?.available && !loadingQuotes[provider.id]) {
      createQuoteForProvider(provider.id, ordersToDispatch[0]);
    }
  };

  const handleSelectProvider = (providerId) => {
    setQuoteData({
      ...quoteData,
      selected: true,
      providerId: providerId,
      quoteInfo: quotes[providerId],
    });
  };

  const checkRide = useCallback(
    async (ifood_id = null) => {
      setLoadingQuotes((prev) => ({ ...prev, ifood: true }));
      const { order_id } = order;

      try {
        if (!ifood_id) {
          ifood_id = merchantId;
        } else {
          setMerchantId(ifood_id);
        }

        const params = {
          oid: order_id,
          mid: ifood_id,
          lat: order.LAT,
          lng: order.LONG,
        };

        const {
          data: { channel_quote_id },
        } = await checkIfoodShippingStatus(params);
        setQuoteId(channel_quote_id);
        setQuoteData({
          merchant_id: ifood_id,
          order_id: order_id,
          quote_id: channel_quote_id,
        });
      } catch (e) {
        console.error(e);
        handleIfoodCheckingModal();
      } finally {
        setLoadingQuotes((prev) => ({ ...prev, ifood: false }));
      }
    },
    [merchantId, order]
  );

  const handleIfoodCheckingModal = useCallback(() => {
    setShowIfoodShipping((prev) => !prev);
  }, []);

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <DeliveryDiningIcon fontSize="small" color="error" />
          <Typography variant="subtitle2" color="text.secondary">
            Entregadores Externos
          </Typography>
        </Stack>

        <motion.div variants={itemVariants}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              overflowX: "auto",
              gap: 2,
              pb: 1,
              "&::-webkit-scrollbar": {
                height: 6,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 3,
              },
            }}
          >
            {deliveryProviders.map((provider) => {
              const isDisabled =
                provider.disabled || isAnotherProviderSelected(provider.id);
              const isSelected = isProviderSelected(provider.id);

              return (
                <Card
                  key={provider.id}
                  elevation={0}
                  component={motion.div}
                  whileHover={
                    isDisabled
                      ? {}
                      : {
                          boxShadow: `0 8px 16px ${alpha(
                            provider.color,
                            0.15
                          )}`,
                        }
                  }
                  whileTap={isDisabled ? {} : { scale: 0.97 }}
                  onClick={() => handleProviderClick(provider)}
                  sx={{
                    width: 150,
                    minWidth: 150,
                    borderRadius: 2,
                    p: 1.5,
                    cursor: isDisabled ? "default" : "pointer",
                    border: `1px solid ${
                      isSelected
                        ? provider.color
                        : alpha(theme.palette.divider, 0.2)
                    }`,
                    opacity: isDisabled ? 0.6 : 1,
                    transition: "all 0.2s ease",
                    backgroundColor: isSelected
                      ? alpha(provider.color, 0.1)
                      : quotes[provider.id]?.available
                      ? alpha(provider.color, 0.05)
                      : "background.paper",
                    "&:hover": {
                      borderColor: isDisabled
                        ? alpha(theme.palette.divider, 0.2)
                        : provider.color,
                    },
                    position: "relative",
                  }}
                >
                  {/* Selected badge/indicator */}
                  {isSelected && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        backgroundColor: provider.color,
                        color: "#fff",
                        borderRadius: "50%",
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        border: `2px solid ${theme.palette.background.paper}`,
                      }}
                    >
                      ✓
                    </Box>
                  )}

                  <Stack spacing={1.5} alignItems="center">
                    {/* Provider Logo */}
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: alpha(provider.color, 0.1),
                        padding: 1,
                      }}
                    >
                      {provider.id === "ifood" ||
                      provider.id === "uber" ||
                      provider.id === "indrive" ? (
                        <img
                          src={provider.logo}
                          alt={provider.name}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            borderRadius: 8,
                          }}
                        />
                      ) : (
                        <Box sx={{ color: provider.color }}>
                          {provider.icon}
                        </Box>
                      )}
                    </Box>

                    {/* Provider Name */}
                    <Typography
                      variant="subtitle2"
                      align="center"
                      fontWeight={600}
                    >
                      {provider.name}
                    </Typography>

                    {/* Provider Description */}
                    <Typography
                      variant="caption"
                      align="center"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        minHeight: 16,
                      }}
                    >
                      {provider.description}
                    </Typography>

                    {/* Quote Information */}
                    {loadingQuotes[provider.id] ? (
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={24}
                        sx={{ borderRadius: 1 }}
                      />
                    ) : quotes[provider.id]?.available ? (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{
                          backgroundColor: alpha(provider.color, 0.1),
                          color: provider.color,
                          borderRadius: 1,
                          py: 0.5,
                          px: 1,
                          width: "100%",
                          justifyContent: "center",
                        }}
                      >
                        <AttachMoneyIcon fontSize="inherit" />
                        <Typography variant="body2" fontWeight={600}>
                          {formatFinance(quotes[provider.id].value)}
                        </Typography>
                      </Stack>
                    ) : quotes[provider.id] ? (
                      <Chip
                        label="Indisponível"
                        size="small"
                        sx={{
                          height: 24,
                          backgroundColor: alpha(theme.palette.error.main, 0.1),
                          color: theme.palette.error.main,
                        }}
                      />
                    ) : (
                      <Chip
                        label="Carregando..."
                        size="small"
                        sx={{
                          height: 24,
                          backgroundColor: alpha(theme.palette.grey[500], 0.1),
                          color: theme.palette.grey[600],
                        }}
                      />
                    )}

                    {/* Time chip - only show if quote is available */}
                    {quotes[provider.id]?.available && (
                      <Tooltip title="Tempo estimado de entrega">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{
                            backgroundColor: alpha(
                              theme.palette.info.main,
                              0.1
                            ),
                            color: theme.palette.info.main,
                            borderRadius: 1,
                            py: 0.5,
                            px: 1,
                            width: "100%",
                            justifyContent: "center",
                          }}
                        >
                          <AccessTimeIcon fontSize="inherit" />
                          <Typography variant="body2" fontWeight={600}>
                            {getEstimatedTime(provider.id)} min
                          </Typography>
                        </Stack>
                      </Tooltip>
                    )}

                    {/* Action Button - only for available quotes that are not selected yet */}
                    {quotes[provider.id]?.available &&
                      !isAnotherProviderSelected(provider.id) &&
                      !isSelected && (
                        <ButtonInfoNoShadow
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectProvider(provider.id);
                          }}
                        >
                          Escolher
                        </ButtonInfoNoShadow>
                      )}

                    {/* Show a "Selected" label if this provider is selected */}
                    {isSelected && (
                      <Chip
                        label="Selecionado"
                        size="small"
                        sx={{
                          height: 24,
                          backgroundColor: alpha(provider.color, 0.2),
                          color: provider.color,
                          fontWeight: "bold",
                        }}
                      />
                    )}
                  </Stack>
                </Card>
              );
            })}
          </Box>
        </motion.div>
      </Box>

      {showIfoodShipping && (
        <CheckShippingModal
          show={showIfoodShipping}
          handleModal={handleIfoodCheckingModal}
          setMerchantId={setMerchantId}
          checkRide={checkRide}
        />
      )}
    </motion.div>
  );
};

export default ShippingProvidersHorizontal;
